"use client";

import { m as motion } from "framer-motion";
import { Mail, MessageSquare, MapPin, Sparkles, ArrowDown } from "lucide-react";
import { trackEvent } from "@/lib/track";

interface ContattiHeroVisualProps {
  isItalian: boolean;
}

// WhatsApp Business Dimensione 4.
const WHATSAPP_NUMBER = "393334404903"; // formato wa.me (senza + / spazi)
const WHATSAPP_DISPLAY = "+39 333 4404 903";

function WhatsAppGlyph({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.004c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 1.8c2.16 0 4.19.84 5.72 2.37a8.06 8.06 0 0 1 2.37 5.74c0 4.47-3.64 8.11-8.12 8.11a8.1 8.1 0 0 1-4.12-1.13l-.3-.18-3.06.8.82-2.98-.2-.31a8.03 8.03 0 0 1-1.24-4.31c0-4.47 3.64-8.11 8.13-8.11Zm4.68 10.3c-.26-.13-1.51-.74-1.74-.83-.23-.09-.4-.13-.57.13-.17.26-.65.83-.8 1-.15.17-.3.19-.55.06-.26-.13-1.08-.4-2.06-1.27-.76-.68-1.28-1.51-1.43-1.77-.15-.26-.02-.4.11-.53.12-.12.26-.3.39-.46.13-.15.17-.26.26-.43.09-.17.04-.32-.02-.45-.06-.13-.57-1.38-.79-1.89-.21-.5-.42-.43-.57-.44l-.49-.01c-.17 0-.45.06-.68.32-.23.26-.89.87-.89 2.12s.91 2.46 1.04 2.63c.13.17 1.79 2.74 4.34 3.84.61.26 1.08.42 1.45.54.61.19 1.16.16 1.6.1.49-.07 1.51-.62 1.72-1.21.21-.6.21-1.11.15-1.21-.06-.11-.23-.17-.49-.3Z" />
    </svg>
  );
}

function scrollToCalendar() {
  if (typeof window === "undefined") return;
  const id = window.innerWidth >= 1280 ? "tidycal-desktop" : "tidycal-mobile";
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function ContattiHeroVisual({ isItalian }: ContattiHeroVisualProps) {
  const leftRows: {
    icon: typeof Mail;
    label: string;
    value: string;
    highlight?: boolean;
    wrap?: boolean;
    href?: string;
  }[] = [
    {
      icon: Mail,
      label: isItalian ? "Email diretta" : "Direct email",
      value: "dariomarcobellini@dimensione4.it",
      highlight: true,
      wrap: true,
      href: "mailto:dariomarcobellini@dimensione4.it",
    },
    {
      icon: MessageSquare,
      label: isItalian ? "Risposta" : "Response",
      value: isItalian ? "< 24h in settimana" : "< 24h on business days",
    },
    {
      icon: MapPin,
      label: isItalian ? "Zona" : "Area",
      value: isItalian ? "Bariano, Bergamo · remoto" : "Bariano, Bergamo · remote",
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15, duration: 0.6 }}
      className="rounded-2xl border border-primary/20 bg-surface shadow-[0_8px_32px_hsl(var(--primary)/0.10)] p-5 select-none"
    >
      {/* Profile header */}
      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-[hsl(var(--border))]">
        <div className="w-10 h-10 rounded-full overflow-hidden shrink-0 border border-primary/30 shadow-[0_0_12px_hsl(var(--primary)/0.25)]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/about/dario-portrait-9x16.webp"
            alt="Dario Marco Bellini"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-foreground">
              Dario Marco Bellini
            </span>
            <span className="relative flex h-1.5 w-1.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--success))] opacity-70" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[hsl(var(--success))]" />
            </span>
            <span className="text-[10px] text-[hsl(var(--success))] font-medium">Online</span>
          </div>
          <div className="text-[11px] text-muted-foreground">
            Fullstack Developer Freelance
            <span className="mx-1 text-primary/60">·</span>
            <span className="text-foreground/70">Founder Dimensione 4</span>
          </div>
        </div>
      </div>

      {/* Due colonne: fatti di contatto (sx) + azioni rapide (dx) */}
      <div className="grid grid-cols-2 gap-x-2 sm:gap-x-4 gap-y-3">
        {/* Colonna sinistra */}
        <div className="space-y-3">
          {leftRows.map((row, i) => {
            const content = (
              <>
                <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5 transition-colors group-hover:bg-primary/20">
                  <row.icon className="w-3.5 h-3.5 text-primary" />
                </div>
                <div className="min-w-0">
                  <div className="text-[9px] font-mono text-muted-foreground/55 uppercase tracking-[0.1em] leading-none mb-0.5">
                    {row.label}
                  </div>
                  <div
                    className={`text-[11px] font-medium ${
                      row.wrap ? "break-all" : "truncate"
                    } ${
                      row.highlight
                        ? "text-primary"
                        : "text-foreground/80"
                    }`}
                  >
                    {row.value}
                  </div>
                </div>
              </>
            );
            const transition = { delay: 0.35 + i * 0.08 };
            return row.href ? (
              <motion.a
                key={i}
                href={row.href}
                onClick={() =>
                  trackEvent("email_click", { location: "contatti_hero" })
                }
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={transition}
                className="pointer-events-auto group flex items-start gap-2.5"
              >
                {content}
              </motion.a>
            ) : (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={transition}
                className="group flex items-start gap-2.5"
              >
                {content}
              </motion.div>
            );
          })}
        </div>

        {/* Colonna destra: WhatsApp + posizionamento + prenota call */}
        <div className="space-y-3">
          <motion.a
            href={`https://wa.me/${WHATSAPP_NUMBER}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() =>
              trackEvent("whatsapp_click", { location: "contatti_hero" })
            }
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="pointer-events-auto group flex items-start gap-2.5"
          >
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5 transition-colors group-hover:bg-primary/20">
              <WhatsAppGlyph className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-[9px] font-mono text-muted-foreground/55 uppercase tracking-[0.1em] leading-none mb-0.5">
                WhatsApp
              </div>
              <div className="text-[11px] font-medium text-foreground/80 transition-colors group-hover:text-primary">
                {WHATSAPP_DISPLAY}
              </div>
            </div>
          </motion.a>

          <motion.div
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.48 }}
            className="flex items-start gap-2.5"
          >
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-[9px] font-mono text-muted-foreground/55 uppercase tracking-[0.1em] leading-none mb-0.5">
                {isItalian ? "Cosa faccio" : "What I do"}
              </div>
              <div className="text-[11px] font-medium text-foreground/80">
                {isItalian ? "Partner per il digitale" : "Your digital partner"}
              </div>
            </div>
          </motion.div>

          <motion.button
            type="button"
            onClick={() => {
              trackEvent("book_call_click", {
                tool: "tidycal",
                location: "contatti_hero_card",
              });
              scrollToCalendar();
            }}
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.56 }}
            className="pointer-events-auto group flex w-full items-start gap-2.5 text-left"
          >
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5 transition-colors group-hover:bg-primary/20">
              <ArrowDown className="w-3.5 h-3.5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="text-[9px] font-mono text-muted-foreground/55 uppercase tracking-[0.1em] leading-none mb-0.5">
                {isItalian ? "Preferisci?" : "Prefer a call?"}
              </div>
              <div className="text-[11px] font-medium text-primary">
                {isItalian ? "Prenota una call veloce" : "Book a quick call"}
              </div>
            </div>
          </motion.button>
        </div>
      </div>

      {/* Footer badge */}
      {/* Stessa griglia a 2 colonne del corpo: il logo parte dove iniziano
          le icone della colonna destra (WhatsApp / Cosa faccio / Preferisci). */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="mt-4 pt-3 border-t border-[hsl(var(--border))] grid grid-cols-2 gap-x-4 items-center"
      >
        <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-foreground">
          <span className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary)/0.6)]" />
          {isItalian ? "Nessuna agenzia, solo io" : "No agency, just me"}
        </span>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/brand/logo-no-payoff.png"
          alt="Dimensione 4"
          className="h-5 w-auto shrink-0 justify-self-start object-contain opacity-80"
        />
      </motion.div>
    </motion.div>
  );
}
